import React, { useState } from 'react';
import UserContextMenu from './UserContextMenu';
import { API_URL, COLORS } from '../config';
import './MemberList.css';

function MemberList({ members, currentUser, onUserContextMenu, servers = [], friends = [], showMobile }) {
  const [contextMenu, setContextMenu] = useState(null);

  const handleRightClick = (e, member) => {
    e.preventDefault();
    setContextMenu({
      user: member,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const handleContextMenuAction = (action) => {
    if (onUserContextMenu && contextMenu) {
      onUserContextMenu(action, contextMenu.user);
    }
    setContextMenu(null);
  };

  // Get the highest priority role color for a member
  const getMemberRoleStyle = (member) => {
    if (!member.roles || member.roles.length === 0) {
      return {};
    }
    
    // Sort by position (highest first) and get the first role with a custom color (not default colors)
    const defaultColors = [COLORS.PRIMARY, COLORS.ROLE_DEFAULT, COLORS.ROLE_MEMBER];
    const highestRole = [...member.roles]
      .sort((a, b) => (b.position || 0) - (a.position || 0))
      .find(role => role.color && !defaultColors.includes(role.color.toLowerCase()));
    
    if (!highestRole) {
      return {};
    }
    
    return {
      color: highestRole.color,
      gradient: highestRole.gradient,
      holographic: highestRole.holographic
    };
  };

  const renderMemberName = (member) => {
    const roleStyle = getMemberRoleStyle(member);
    const classNames = ['member-name'];
    
    if (roleStyle.holographic) {
      classNames.push('holographic');
    }
    if (roleStyle.gradient) {
      classNames.push('gradient-text');
    }
    
    const style = {};
    if (roleStyle.gradient) {
      style['--role-gradient'] = roleStyle.gradient;
    } else if (roleStyle.color) {
      style.color = roleStyle.color;
    }
    
    return (
      <div className={classNames.join(' ')} style={style}>
        {member.username}
      </div>
    );
  };

  return (
    <div className={`member-list ${showMobile ? 'mobile-visible' : ''}`}>
      <div className="member-list-header">
        Members — {members.length}
      </div>
      <div className="members-scroll">
        <div className="member-category">
          <div className="member-category-header">
            ONLINE — {members.filter(m => m.status === 'online').length}
          </div>
          {members
            .filter(m => m.status === 'online')
            .map(member => (
              <div 
                key={member.id} 
                className="member-item"
                onContextMenu={(e) => handleRightClick(e, member)}
              >
                <div className="member-avatar">
                  <div className="avatar-circle">
                    {member.avatar_url ? (
                      <img 
                        src={`${API_URL}${member.avatar_url}`} 
                        alt={member.username}
                        className="avatar-img"
                      />
                    ) : (
                      member.username.substring(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className={`status-indicator ${member.status || 'offline'}`}></div>
                </div>
                {renderMemberName(member)}
              </div>
            ))}
        </div>

        {members.filter(m => m.status !== 'online').length > 0 && (
          <div className="member-category">
            <div className="member-category-header">
              OFFLINE — {members.filter(m => m.status !== 'online').length}
            </div>
            {members
              .filter(m => m.status !== 'online')
              .map(member => (
                <div 
                  key={member.id} 
                  className="member-item offline"
                  onContextMenu={(e) => handleRightClick(e, member)}
                >
                  <div className="member-avatar">
                    <div className="avatar-circle">
                      {member.avatar_url ? (
                        <img 
                          src={`${API_URL}${member.avatar_url}`} 
                          alt={member.username}
                          className="avatar-img"
                        />
                      ) : (
                        member.username.substring(0, 1).toUpperCase()
                      )}
                    </div>
                    <div className={`status-indicator ${member.status || 'offline'}`}></div>
                  </div>
                  {renderMemberName(member)}
                </div>
              ))}
          </div>
        )}
      </div>

      {contextMenu && (
        <UserContextMenu
          user={contextMenu.user}
          currentUser={currentUser}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onProfile={() => handleContextMenuAction('profile')}
          onMessage={() => handleContextMenuAction('message')}
          onInviteToServer={(serverId) => handleContextMenuAction('invite', serverId)}
          onAddFriend={() => handleContextMenuAction('addFriend')}
          onBlock={() => handleContextMenuAction('block')}
          servers={servers}
          friends={friends}
        />
      )}
    </div>
  );
}

export default MemberList;
